import {
  QUIZ_PROTOCOL_VERSION,
  type QuizClient,
  type QuizEvent,
  type QuizJoinOpts,
  type QuizRoomState,
  type QuizSettings,
} from "./types";

/**
 * Quizzle WebSocket transport → QuizRoom Durable Object at /quiz/ws/<CODE>.
 * Mirrors the runner's WebSocketTransport (versioned hello, 1.5s heartbeat,
 * auto-reconnect) but with the Quizzle message set.
 */
const HEARTBEAT_MS = 1500;

export class QuizTransport implements QuizClient {
  private ws: WebSocket | null = null;
  private url = "";
  private myId = "";
  private listeners: Array<(e: QuizEvent) => void> = [];
  private hello: QuizJoinOpts | null = null;
  private heartbeat: number | null = null;
  private reconnects = 0;

  async join(opts: QuizJoinOpts): Promise<QuizRoomState> {
    this.myId = opts.me.id;
    this.hello = opts;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/quiz/ws/${opts.code}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Quizzle server didn't respond")), 6000);
      const off = this.on((e) => {
        if (e.type === "state") {
          // Host sees its own state echo; controllers wait until they appear.
          const joined = opts.role === "host" || e.state.players.some((p) => p.id === this.myId);
          if (joined) { clearTimeout(timer); off(); resolve(e.state); }
        } else if (e.type === "error") {
          clearTimeout(timer); off(); reject(new Error(e.message));
        }
      });
      this.connect();
    });
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnects = 0;
      if (this.hello) {
        this.send({
          type: "hello",
          v: QUIZ_PROTOCOL_VERSION,
          role: this.hello.role,
          me: this.hello.me,
          settings: this.hello.settings,
        });
      }
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") this.emit({ type: "state", state: msg.state });
        else if (msg.type === "error") this.emit({ type: "error", message: msg.message ?? "Quizzle error" });
        // pong: heartbeat ack, nothing to do
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
      if (this.reconnects < 5 && this.hello) {
        this.reconnects++;
        setTimeout(() => this.connect(), Math.min(2000, 200 * 2 ** this.reconnects));
      }
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => this.send({ type: "ping", at: Date.now() }), HEARTBEAT_MS);
  }
  private stopHeartbeat() {
    if (this.heartbeat != null) { window.clearInterval(this.heartbeat); this.heartbeat = null; }
  }

  setSettings(settings: Partial<QuizSettings>): void {
    this.send({ type: "set-settings", settings });
  }
  start(pool: import("@shared/types").WordDTO[]): void {
    this.send({ type: "start", pool });
  }
  answer(correct: boolean, elapsedMs: number): void {
    this.send({ type: "answer", correct, elapsedMs });
  }
  next(): void {
    this.send({ type: "next" });
  }
  toLobby(): void {
    this.send({ type: "to-lobby" });
  }

  on(cb: (e: QuizEvent) => void): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter((l) => l !== cb); };
  }

  leave(): void {
    this.send({ type: "leave" });
    this.stopHeartbeat();
    this.hello = null;
    if (this.ws) { try { this.ws.close(); } catch { /* ignore */ } this.ws = null; }
    this.listeners = [];
  }

  private send(msg: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(msg)); } catch { /* reconnect handles it */ }
  }
  private emit(e: QuizEvent) { for (const l of this.listeners) l(e); }
}
